import { ChangeDetectionStrategy, Component, effect, inject, input, model, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Dialog } from 'primeng/dialog';
import {
	SaveHistoryService,
	type SaveEventMeta,
	type DiffEntry,
} from '../../../features/editor/services/save-history.service';

/**
 * Save-history viewer: lists the append-only `save_events` journal for a project
 * and, on selecting an entry, shows the field-level diff vs the previous save of
 * the same file (only the things that changed).
 */
@Component({
	selector: 'app-save-history-dialog',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [Dialog, DatePipe],
	templateUrl: './save-history-dialog.html',
	styleUrl: './save-history-dialog.scss',
})
export class SaveHistoryDialogComponent {
	visible = model(false);
	projectSlug = input<string>('');

	private historyService = inject(SaveHistoryService);

	protected loading = signal(false);
	protected events = signal<SaveEventMeta[]>([]);
	protected selectedId = signal<string | null>(null);
	protected diffLoading = signal(false);
	protected diff = signal<DiffEntry[] | null>(null);
	protected hasPrevious = signal(true);
	protected error = signal<string | null>(null);

	constructor() {
		// Load the journal fresh each time the dialog opens.
		effect(() => {
			if (this.visible()) this.loadEvents();
		});
	}

	private async loadEvents(): Promise<void> {
		const slug = this.projectSlug();
		if (!slug) return;
		this.loading.set(true);
		this.error.set(null);
		this.diff.set(null);
		this.selectedId.set(null);
		try {
			this.events.set(await this.historyService.list(slug));
		} catch {
			this.error.set('Could not load the save history.');
		} finally {
			this.loading.set(false);
		}
	}

	protected async selectEvent(event: SaveEventMeta): Promise<void> {
		this.selectedId.set(event._id);
		this.diffLoading.set(true);
		this.diff.set(null);
		try {
			const result = await this.historyService.diff(this.projectSlug(), event._id);
			this.diff.set(result.diff);
			this.hasPrevious.set(result.hasPrevious);
		} catch {
			this.error.set('Could not load the diff.');
		} finally {
			this.diffLoading.set(false);
		}
	}

	/** Short human label for a file path (last two segments). */
	protected fileLabel(path: string): string {
		const parts = path.split('/').filter(Boolean);
		if (parts.length <= 1) return path;
		// e.g. .../scenes/intro/scene.json → "intro/scene.json"
		return parts.slice(-2).join('/');
	}

	/** Compact rendering of a diff value; `undefined` means the field was absent on that side. */
	protected fmt(value: unknown): string {
		if (value === undefined) return '∅';
		if (typeof value === 'string') return value;
		return JSON.stringify(value);
	}
}
